import { put, takeLatest, call } from "redux-saga/effects";
import AuthActions, { Types as AuthTypes } from "../../stores/auth/Actions";
import AuthService from "../../services/auth/AuthService";
import PrestadorService from "../../services/prestador/PrestadorService";
import ResponseCode from "../../services/ResponseCode";
import { getPermissionsForRoles } from "../../utils/RolePermissionsMapping";

import { LOCAL_STORAGE, PSS_TYPE } from "../../utils/Constant";
import { RESPUESTA_PILOTO_CIERRE_CUENTAS } from "../../containers/ingreso/utils/IngresoConstant";

function* authUser(request) {
  yield put(AuthActions.authUserLoading());

  const { codigo, pin } = request.payload;

  const requestToken = {
    username: codigo,
    password: pin,
    compania: process.env.REACT_APP_COMPANY,
  };

  const tokenResponse = yield call(AuthService.getToken, requestToken);

  if (!tokenResponse?.respuesta) {
    yield put(
      AuthActions.authUserFailure({
        codigo: ResponseCode.SERVER_ERROR.code,
        mensaje: ResponseCode.SERVER_ERROR.message,
      }),
    );
    return;
  }

  if (
    tokenResponse?.respuesta?.codigo !== ResponseCode.USER_AUTHENTICATED.code
  ) {
    yield put(
      AuthActions.authUserFailure({
        codigo: ResponseCode.AUTHENTICATION_FAILED.code,
        mensaje: ResponseCode.AUTHENTICATION_FAILED.message,
      }),
    );
    return;
  }

  const isEmailUser = codigo.includes("@");

  let sesionResponse, userResponse, numeroSesion;

  if (isEmailUser) {
    userResponse = yield call(
      AuthService.getUsersMe,
      AuthService.handleRequestAuth(null, tokenResponse?.data?.accessToken),
    );

    if (!userResponse?.response) {
      yield put(
        AuthActions.authUserFailure({
          codigo: ResponseCode.SERVER_ERROR.code,
          mensaje: ResponseCode.SERVER_ERROR.message,
        }),
      );
      return;
    }

    if (userResponse?.response?.code !== ResponseCode.USER_AUTHENTICATED.code) {
      yield put(
        AuthActions.authUserFailure({
          codigo: userResponse?.response?.code,
          mensaje: userResponse?.response?.message,
        }),
      );
      return;
    }
  } else {
    sesionResponse = yield call(
      AuthService.newSession,
      AuthService.handleRequestAuthPin(
        { codigo },
        tokenResponse?.data?.accessToken,
        pin,
      ),
    );

    if (!sesionResponse?.respuesta) {
      yield put(
        AuthActions.authUserFailure({
          codigo: ResponseCode.SERVER_ERROR.code,
          mensaje: ResponseCode.SERVER_ERROR.message,
        }),
      );
      return;
    }

    if (sesionResponse?.respuesta?.codigo === ResponseCode.SERVER_ERROR.code) {
      yield put(
        AuthActions.authUserFailure({
          codigo: ResponseCode.SERVER_ERROR.code,
          mensaje: ResponseCode.SERVER_ERROR.message,
        }),
      );
      return;
    }

    if (sesionResponse?.respuesta?.codigo !== ResponseCode.OK.code) {
      yield put(AuthActions.authUserFailure(sesionResponse?.respuesta));
      return;
    }

    numeroSesion = sesionResponse?.data?.numeroSesion;

    userResponse = yield call(
      AuthService.getUserInfo,
      AuthService.handleRequestAuth(null, tokenResponse?.data?.accessToken),
    );

    if (!userResponse?.respuesta) {
      yield put(
        AuthActions.authUserFailure({
          codigo: ResponseCode.SERVER_ERROR.code,
          mensaje: ResponseCode.SERVER_ERROR.message,
        }),
      );
      return;
    }

    if (userResponse?.respuesta?.codigo === ResponseCode.SERVER_ERROR.code) {
      yield put(
        AuthActions.authUserFailure({
          codigo: ResponseCode.SERVER_ERROR.code,
          mensaje: ResponseCode.SERVER_ERROR.message,
        }),
      );
      return;
    }

    if (userResponse?.respuesta?.codigo !== ResponseCode.OK.code) {
      yield put(AuthActions.authUserFailure(userResponse?.respuesta));
      return;
    }

    if (Number(userResponse?.data?.prestador?.codigo) !== Number(codigo)) {
      yield put(
        AuthActions.authUserFailure({
          codigo: ResponseCode.AUTHENTICATION_FAILED.code,
          mensaje: ResponseCode.AUTHENTICATION_FAILED.message,
        }),
      );
      return;
    }
  }

  const esCierreCuenta = userResponse?.data?.prestador?.esCierreCuenta;

  const esPssNoMedico =
    userResponse?.data?.prestador?.tipoPss?.toLowerCase() ===
    PSS_TYPE.NO_MEDICO.toLowerCase();

  let configuracionPaperFree,
    esPaquete = false,
    nivelAtencionPermitido = false;

  if (!isEmailUser) {
    const requestConfigPaperFree = {
      codigoPss: codigo,
      tipoPss: userResponse?.data?.prestador?.tipoPss,
    };

    configuracionPaperFree = yield call(
      AuthService.getConfiguracionPaperFree,
      AuthService.handleRequestAuth(
        requestConfigPaperFree,
        tokenResponse?.data?.accessToken,
      ),
    );

    if (
      PSS_TYPE.NO_MEDICO.toLocaleLowerCase() ===
      userResponse?.data?.prestador?.tipoPss?.toLocaleLowerCase()
    ) {
      const validarPaqueteRequest = {
        codigo: userResponse?.data?.prestador?.codigo,
      };

      const validarPaqueteResponse = yield call(
        PrestadorService.validarPaquete,
        validarPaqueteRequest,
      );

      esPaquete = validarPaqueteResponse?.data?.esPaquete;

      const validarNivelRequest = {
        tipoReclamante: PSS_TYPE.NO_MEDICO,
        reclamante: userResponse?.data?.prestador?.codigo,
      };

      const validarNivelResponse = yield call(
        PrestadorService.validarNivelAtencion,
        validarNivelRequest,
      );

      nivelAtencionPermitido = validarNivelResponse?.data?.categoria !== null;
    }
  }

  const userRoles = userResponse?.data?.roles || [];
  const roleBasedPermissions = getPermissionsForRoles(userRoles);
  const explicitPermissions = userResponse?.data?.permissions || [];

  const allPermissions = [
    ...new Set([...roleBasedPermissions, ...explicitPermissions]),
  ];

  const userData = isEmailUser
    ? {
        prestador: {
          nombre:
            userResponse?.data?.profile?.name &&
            userResponse?.data?.profile?.lastName
              ? `${userResponse.data.profile.name} ${userResponse.data.profile.lastName}`.trim()
              : userResponse?.data?.username || codigo,
          codigo: codigo,
          email: userResponse?.data?.email,
          isEmailUser: true,
          roles: userRoles,
          permisos: allPermissions,
        },
        ccPermitido: false,
        configuracionPaperFree: null,
        isAuth: true,
        numeroSesionReutilizable: null,
        isStandVirtual: true,
        tokenStandVirtual: tokenResponse?.data?.accessToken,
        isStandVirtualAdmin: userRoles.some((role) =>
          ["master", "admin", "administrador", "monitor"].includes(
            role.toLowerCase(),
          ),
        ),
        isEmailUser: true,
      }
    : {
        prestador: {
          ...userResponse?.data?.prestador,
          esPaquete,
          permiteCierreCuenta:
            esPssNoMedico &&
            nivelAtencionPermitido &&
            (esCierreCuenta === RESPUESTA_PILOTO_CIERRE_CUENTAS.AMBOS ||
              esCierreCuenta === RESPUESTA_PILOTO_CIERRE_CUENTAS.CIERRE_CUENTA),
          permiteHojaQuirurgica:
            esPssNoMedico &&
            nivelAtencionPermitido &&
            (esCierreCuenta === RESPUESTA_PILOTO_CIERRE_CUENTAS.AMBOS ||
              esCierreCuenta ===
                RESPUESTA_PILOTO_CIERRE_CUENTAS.HOJA_QUIRURGICA),
        },
        ccPermitido: userResponse?.data?.prestador?.Cc,
        configuracionPaperFree,
        isAuth: true,
        numeroSesionReutilizable: numeroSesion,
        isStandVirtual: false,
        tokenStandVirtual: null,
        isStandVirtualAdmin: false,
        isEmailUser: false,
      };

  yield put(AuthActions.authUserSuccess(userData));
}

function* nuevaSesion(request) {
  yield put(AuthActions.nuevaSesionLoading());

  const { codigo } = request.payload;

  const newSessionRequest = {
    codigo,
  };

  const sesionResponse = yield call(
    AuthService.newSessionWithNoHeader,
    newSessionRequest,
  );

  if (
    !sesionResponse?.respuesta ||
    sesionResponse?.respuesta?.codigo === ResponseCode.SERVER_ERROR.code
  ) {
    yield put(
      AuthActions.nuevaSesionFailure({
        codigo: ResponseCode.SERVER_ERROR.code,
        mensaje: ResponseCode.SERVER_ERROR.message,
      }),
    );
    return;
  }

  if (sesionResponse?.respuesta?.codigo !== ResponseCode.OK.code) {
    yield put(AuthActions.nuevaSesionFailure(sesionResponse?.respuesta));
    return;
  }

  const { numeroSesion } = sesionResponse?.data;

  const { respuesta } = sesionResponse;

  yield put(AuthActions.nuevaSesionSuccess({ numeroSesion, respuesta }));
}

function* logoutUser() {
  yield call(AuthService.logOut, { compania: process.env.REACT_APP_COMPANY });

  localStorage.clear();

  localStorage.removeItem(LOCAL_STORAGE.ESTADO_SESION.KEY);

  localStorage.removeItem(LOCAL_STORAGE.PSS_ACTIVO.KEY);

  yield put(AuthActions.logoutUserSuccess({}));

  AuthService.redirectToLogin();
}

function* clearSesion() {
  yield put(AuthActions.clearSesionSuccess({}));
}

function* clearLogin() {
  localStorage.removeItem(LOCAL_STORAGE.ESTADO_SESION.KEY);

  localStorage.removeItem(LOCAL_STORAGE.PSS_ACTIVO.KEY);

  yield put(AuthActions.clearLoginSuccess({}));
}

const exportedArray = [
  takeLatest(AuthTypes.AUTH_USER, authUser),
  takeLatest(AuthTypes.NUEVA_SESION, nuevaSesion),
  takeLatest(AuthTypes.LOGOUT_USER, logoutUser),
  takeLatest(AuthTypes.CLEAR_SESION, clearSesion),
  takeLatest(AuthTypes.CLEAR_LOGIN, clearLogin),
];

export default exportedArray;
